'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { trackFunnelEvent } from '@/lib/analytics';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

// Used only if the bar can't be measured (no layout yet / unsupported). Roughly
// the real zone: bottom nav (~5rem) + collapsed bar (~3.5rem).
const FALLBACK_DOCK_ZONE_PX = 136;

/**
 * Height of the screen region the collapsed bar occupies, measured from the
 * viewport bottom. Both reads are transform-independent (`offsetHeight` is the
 * border-box layout height; computed `bottom` resolves the CSS calc() to px),
 * so this stays correct even when the bar is parked off-screen by a transform.
 */
const measureDockZone = (bar: HTMLElement): number => {
  const bottom = Number.parseFloat(getComputedStyle(bar).bottom);
  const zone = (Number.isFinite(bottom) ? bottom : 0) + bar.offsetHeight;
  return zone > 0 ? Math.round(zone) : FALLBACK_DOCK_ZONE_PX;
};

/**
 * The page scrolls in `.contentArea`, not on <body> (globals.scss pins
 * html/body to `overflow: hidden`), so scroll-locking means locking that
 * ancestor. `overflow: hidden` keeps the element a scroll container, so its
 * scrollTop survives the lock and the page doesn't jump on close.
 */
const findScrollParent = (node: HTMLElement | null): HTMLElement | null => {
  let current = node?.parentElement ?? null;
  while (current) {
    const { overflowY } = getComputedStyle(current);
    if (overflowY === 'auto' || overflowY === 'scroll') return current;
    current = current.parentElement;
  }
  return null;
};

export type UseWaitlistDockProps = {
  /** False on surfaces that want the plain inline card (e.g. the cost page). */
  enabled: boolean;
  breedSlug: string;
  provinceSlug?: string;
  citySlug?: string;
};

export function useWaitlistDock({
  enabled,
  breedSlug,
  provinceSlug,
  citySlug
}: UseWaitlistDockProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLButtonElement>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  // Has the inline card scrolled up into the bar's zone? Starts false so the
  // server and first client render agree (the widget sits below the fold on
  // every docked page); the observer corrects it immediately after mount.
  const [hasReachedInlineCard, setHasReachedInlineCard] = useState(false);
  const [placeholderHeight, setPlaceholderHeight] = useState<number | null>(
    null
  );
  // The bar is server-rendered visible (right for every page where the card is
  // below the fold). When it isn't — a breed with no listings puts the card on
  // screen immediately — the observer hides it on hydration, and that first
  // correction should be instant rather than a 240ms slide-away.
  const [hasSettled, setHasSettled] = useState(false);
  const hasSettledRef = useRef(false);
  // Mirrors the state above so `collapse` can check bar visibility without
  // taking it as a dependency (which would rebuild the key handlers on every
  // handoff).
  const hasReachedInlineCardRef = useRef(false);

  const trackEvent = useCallback(
    (eventName: 'waitlist_bar_expand' | 'waitlist_bar_collapse') => {
      trackFunnelEvent(eventName, {
        signup_source: 'global_waitlist',
        breed_slug: breedSlug,
        province_slug: provinceSlug || undefined,
        city_slug: citySlug || undefined,
        surface: 'waitlist_dock_bar'
      });
    },
    [breedSlug, provinceSlug, citySlug]
  );

  const expand = useCallback(() => {
    // Freeze the section at its current height before the card leaves the flow,
    // so content below it doesn't slide up behind the overlay (and back down on
    // close, jumping the scroll position).
    const section = sectionRef.current;
    if (section) setPlaceholderHeight(section.offsetHeight);
    setIsExpanded(true);
    trackEvent('waitlist_bar_expand');
  }, [trackEvent]);

  const collapse = useCallback(() => {
    setIsExpanded(false);
    setPlaceholderHeight(null);
    trackEvent('waitlist_bar_collapse');
    // Hand focus back to the bar, but only when it's the surface the user is
    // returning to — if the inline card is on screen the bar stays hidden and
    // must not take focus while aria-hidden.
    if (!hasReachedInlineCardRef.current) barRef.current?.focus();
  }, [trackEvent]);

  // Hand off between the bar and the inline card as the card scrolls into the
  // bar's zone. IntersectionObserver only — no scroll listeners, no rAF, and no
  // layout reads while scrolling, so this costs nothing on the main thread.
  useEffect(() => {
    if (!enabled) return;

    const section = sectionRef.current;
    const bar = barRef.current;
    if (!section) return;

    if (typeof IntersectionObserver === 'undefined') {
      // No observer: fall back to the plain inline card rather than stranding a
      // bar that would never hand off.
      hasReachedInlineCardRef.current = true;
      setHasReachedInlineCard(true);
      hasSettledRef.current = true;
      setHasSettled(true);
      return;
    }

    // Measured once per mount. The zone only changes at the mobileLg
    // breakpoint, so a resize listener would buy ~8px of handoff accuracy at
    // the cost of running work during scroll-driven mobile viewport resizes.
    const dockZone = bar ? measureDockZone(bar) : FALLBACK_DOCK_ZONE_PX;

    let rafId = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        hasReachedInlineCardRef.current = entry.isIntersecting;
        setHasReachedInlineCard(entry.isIntersecting);
        // Enable transitions only AFTER the first observation has painted.
        // If we flipped hasSettled in this same commit, the transition would be
        // live for the very first hide and the bar would slide away on load
        // (on pages where the card is already on screen) instead of just not
        // being there. One frame's delay makes that first correction a snap.
        if (!hasSettledRef.current) {
          hasSettledRef.current = true;
          rafId = requestAnimationFrame(() => setHasSettled(true));
        }
      },
      { rootMargin: `0px 0px -${dockZone}px 0px`, threshold: 0 }
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [enabled]);

  // Lock the scrolling ancestor while the overlay covers the page.
  useEffect(() => {
    if (!isExpanded) return;

    const scrollParent = findScrollParent(sectionRef.current);
    if (!scrollParent) return;

    const previousOverflow = scrollParent.style.overflow;
    scrollParent.style.overflow = 'hidden';
    return () => {
      scrollParent.style.overflow = previousOverflow;
    };
  }, [isExpanded]);

  // Move focus into the dialog so keyboard and screen-reader users land on it.
  useEffect(() => {
    if (!isExpanded) return;
    panelRef.current?.focus();
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') collapse();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, collapse]);

  // Focus trap. Focusables are queried per Tab rather than cached on open,
  // because the form grows an intro-message field once the user fills in their
  // name, email and location.
  useEffect(() => {
    if (!isExpanded) return;

    const panel = panelRef.current;
    if (!panel) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      );
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    panel.addEventListener('keydown', handleKeyDown);
    return () => panel.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded]);

  return {
    isDocked: enabled,
    isExpanded,
    /** The bar yields to the inline card once you scroll down to it. */
    isBarVisible: enabled && !isExpanded && !hasReachedInlineCard,
    /** Gates the bar's transition so the first post-hydration state is instant. */
    hasSettled,
    placeholderHeight,
    sectionRef,
    panelRef,
    barRef,
    expand,
    collapse
  };
}
